/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import { observer } from "mobx-react";
import styled from "styled-components";
import { Link } from "react-router";
import { ArrowRight, ArrowUpRight, Copy, Rocket, Truck } from "lucide-react";

import IconTile from "@src/components/ui/IconTile";
import MigrationFlow from "@src/components/modules/LiveModule/MigrationFlow";
import PhaseStepper from "@src/components/modules/LiveModule/PhaseStepper";
import DiskRows from "@src/components/modules/LiveModule/DiskRows";
import Sparkline from "@src/components/modules/LiveModule/Sparkline";
import { Bar, Chip, Label } from "@src/components/modules/LiveModule/ui/LiveUi";
import type { LiveEntry, ThroughputSample } from "@src/components/modules/LiveModule/LiveStore";
import {
  LIVE,
  endpointOf,
  formatClock,
  phaseTone,
  targetFill,
  providerLabel,
} from "@src/components/modules/LiveModule/liveUtils";
import {
  formatBytes,
  formatDuration,
  formatRate,
  isActiveStatus,
  parseApiDate,
} from "@src/utils/TransferProgress";

const Wrapper = styled.article`
  background: ${LIVE.surface};
  border: 1px solid ${LIVE.border};
  border-radius: 8px;
  overflow: hidden;
`;
const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  border-bottom: 1px solid ${LIVE.border};
`;
const Titles = styled.div`
  flex-grow: 1;
  min-width: 0;
`;
const Name = styled(Link)`
  display: block;
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  color: ${LIVE.text};
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    text-decoration: underline;
  }
`;
const Meta = styled.div`
  margin-top: 2px;
  font-size: 12.5px;
  color: ${LIVE.muted};
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  b {
    color: ${LIVE.textSecondary};
    font-weight: 600;
  }
`;
const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;
const OpenLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid ${LIVE.border};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${LIVE.slate};
  text-decoration: none;
  background: var(--t9-bg-surface);
  &:hover {
    border-color: ${LIVE.slate};
  }
`;
const StepperRow = styled.div`
  padding: 16px 24px 4px 24px;
`;
const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(360px, 1.15fr) minmax(320px, 1fr);
  gap: 8px 32px;
  align-items: center;
  padding: 8px 24px 16px 24px;
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;
const FlowCell = styled.div`
  display: flex;
  justify-content: center;
  min-width: 0;
`;
const Stats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;
const Overall = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const OverallTop = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  .pct {
    font-size: 34px;
    line-height: 38px;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: ${LIVE.text};
    font-variant-numeric: tabular-nums;
  }
  .of {
    font-size: 13px;
    color: ${LIVE.muted};
    b {
      color: ${LIVE.text};
      font-weight: 600;
    }
  }
`;
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
`;
const Stat = styled.div`
  border: 1px solid ${LIVE.border};
  border-radius: 8px;
  padding: 10px 12px;
  min-width: 0;
  .v {
    margin-top: 2px;
    font-size: 18px;
    line-height: 24px;
    font-weight: 700;
    color: ${LIVE.text};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .s {
    font-size: 12px;
    color: ${LIVE.muted};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const Disks = styled.div`
  padding: 4px 24px 18px 24px;
`;

const instanceSummary = (entry: LiveEntry) => {
  const n = entry.instances.length;
  return n === 1 ? entry.instances[0].split("/").pop() : `${n} machines`;
};

export const statusLabel = (entry: LiveEntry) => {
  const { phase } = entry.progress;
  if (phase === "Completed") {
    return "Completed";
  }
  if (phase === "Failed") {
    return "Failed";
  }
  if (phase === "Cancelled") {
    return "Cancelled";
  }
  if (entry.status === "CANCELLING") {
    return "Cancelling";
  }
  return phase;
};

export const runLabel = (entry: LiveEntry) => {
  if (entry.itemType === "deployment") {
    return "Deployment";
  }
  const what = entry.kind === "replica" ? "Sync" : "Migration run";
  return entry.executionNumber != null ? `${what} #${entry.executionNumber}` : what;
};

type Props = {
  entry: LiveEntry;
  samples: ThroughputSample[];
  now: number;
};

const LiveExecutionCard = observer(({ entry, samples, now }: Props) => {
  const p = entry.progress;
  const source = endpointOf(entry.sourceEndpointId);
  const target = endpointOf(entry.targetEndpointId);
  const active = isActiveStatus(entry.status);
  const tone = phaseTone(p.phase);
  const finishAt = active && p.etaSeconds != null ? now + p.etaSeconds * 1000 : null;
  const startedAt = parseApiDate(entry.createdAt);
  const elapsed = active && startedAt ? (now - startedAt) / 1000 : p.elapsedSeconds;
  const typeIcon = entry.itemType === "deployment" ? Rocket : entry.kind === "replica" ? Copy : Truck;
  const rates = samples.map(s => s.rate);
  const copying = p.disks.filter(d => d.active).length;

  return (
    <Wrapper aria-label={entry.name}>
      <Header>
        <IconTile icon={typeIcon} tone={entry.kind === "replica" ? "info" : "brand"} size={40} />
        <Titles>
          <Name to={entry.link} title={entry.name}>
            {entry.name}
          </Name>
          <Meta>
            <b>{runLabel(entry)}</b>
            <span>·</span>
            <span>{instanceSummary(entry)}</span>
            <span>·</span>
            <span>{source ? source.name : providerLabel(null)}</span>
            <ArrowRight size={13} aria-label="to" />
            <span>{target ? target.name : providerLabel(null)}</span>
          </Meta>
        </Titles>
        <Right>
          <Chip tone={tone} pulse={active && p.phase === "Syncing disks"}>
            {statusLabel(entry)}
          </Chip>
          <OpenLink to={entry.link}>
            Details <ArrowUpRight size={14} />
          </OpenLink>
        </Right>
      </Header>
      <StepperRow>
        <PhaseStepper phases={p.phases} phase={p.phase} phaseIndex={p.phaseIndex} />
      </StepperRow>
      <Body>
        <FlowCell>
          <MigrationFlow
            sourceType={source?.type}
            targetType={target?.type}
            phase={p.phase}
            percent={targetFill(p)}
            rateBytesPerSec={active ? p.rateBytesPerSec : 0}
            activeDisks={copying}
          />
        </FlowCell>
        <Stats>
          <Overall>
            <OverallTop>
              <span className="pct">{Math.floor(p.percent)}%</span>
              <span className="of">
                {p.hasDiskData ? (
                  <>
                    <b>{formatBytes(p.doneBytes)}</b> of {formatBytes(p.totalBytes)} copied
                  </>
                ) : (
                  <>
                    <b>{p.tasksDone}</b> of {p.tasksTotal} steps done
                  </>
                )}
              </span>
            </OverallTop>
            <Bar
              percent={p.percent}
              height={10}
              tone={p.phase === "Completed" ? "success" : p.phase === "Failed" ? "danger" : "info"}
              moving={active && p.rateBytesPerSec > 0}
              label="Overall progress"
            />
          </Overall>
          <StatGrid>
            <Stat>
              <Label>Throughput</Label>
              <div className="v">{active && p.rateBytesPerSec > 0 ? formatRate(p.rateBytesPerSec) : "—"}</div>
              <div style={{ marginTop: 4 }}>
                <Sparkline values={rates.length ? rates : [0]} height={26} label="Throughput, last few minutes" min={0} />
              </div>
            </Stat>
            <Stat>
              <Label>Time left</Label>
              <div className="v">
                {!active
                  ? p.phase === "Completed"
                    ? "Done"
                    : "—"
                  : p.etaSeconds != null
                    ? `~${formatDuration(p.etaSeconds)}`
                    : "Estimating…"}
              </div>
              <div className="s">
                {finishAt
                  ? `Finishes about ${formatClock(finishAt, now)}`
                  : active
                    ? p.hasDiskData
                      ? "Once the copy picks up speed"
                      : p.disks.length
                        ? "Once the disk copy starts"
                        : "No disk copy in this run"
                    : `Ended ${formatClock(parseApiDate(entry.updatedAt), now)}`}
              </div>
            </Stat>
            <Stat>
              <Label>Elapsed</Label>
              <div className="v">{formatDuration(elapsed)}</div>
              <div className="s">Started {formatClock(startedAt, now)}</div>
            </Stat>
            <Stat>
              <Label>Disks</Label>
              <div className="v">
                {p.disks.length ? `${p.completedDisks} of ${p.disks.filter(d => !d.scanning).length} done` : "—"}
              </div>
              <div className="s">
                {copying ? `${copying} copying now` : p.disks.length ? "None copying now" : "Not reported yet"}
              </div>
            </Stat>
          </StatGrid>
        </Stats>
      </Body>
      {p.disks.length ? (
        <Disks>
          <DiskRows disks={p.disks} showInstance={p.instances.length > 1} />
        </Disks>
      ) : null}
    </Wrapper>
  );
});

export default LiveExecutionCard;
